import React, {
  createRef, useEffect, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IconInfoCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { ReactComponent as IconError } from "src/assets/icons/toast-error.svg";
import { ReactComponent as IconSuccess } from "src/assets/icons/toast-success.svg";
import { useDisclosure } from '@mantine/hooks';
import useCleverTapEvents from 'src/events/useGetEvents';
import { SIGNATURE_EVENT } from 'src/events/constant';
import { Button } from '@mantine/core';
import { UploadIcon } from 'src/assets/svg';
import { RootState } from '../../redux/store';
import { ScreenWrapper } from '../../components';
import { OnboardingProgress } from '../../components/onboardingProgress';
import { useStyles } from './signOnboardingForm.styles';
import { DrawSignModal } from './modals/drawSignModal';
import {
  clearDigioFormError, saveUserDeclaration, saveUserSign, setDigioFormStatus,
} from './digioFormSlice';
import { ScreenLoader } from '../../components/screenLoader';
import {
  clearFnoActivationFormError,
  setFnoActivationFormStatus,
} from '../fnoActivationForm/fnoActivationFormSlice';
import { getProgressStepper } from '../../utils/helpers';
import PreviewSignatureModal from './modals/previewSignature';

export const SignOnboardingForm = () => {
  const { classes, cx } = useStyles();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { TriggerEvent } = useCleverTapEvents();
  const currentStep = useSelector((state: RootState) => state.account.userDetails.currentStep);
  const signFile = useRef<File | null>(null);
  const digioState = useSelector((state: RootState) => state.digioData);
  const accountState = useSelector((state: RootState) => state.account);
  const fnoActivationState = useSelector((state: RootState) => state.fnoActivationDetails);
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [isPoliticallyNotExposed, setIsPoliticallyNotExposed] = useState(true);
  const [isIndianCitizen, setIsIndianCitizen] = useState(true);
  const fileRef = createRef<HTMLInputElement>();
  const sigCanvas = useRef<any>(null);
  const checkState = accountState.userDetails?.signatureUploaded || digioState.isUserDeclarationSaved;
  const [isPreviewSignOpened, { open: previewSignOpen, close: previewSignClose }] = useDisclosure(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

  const onNextClick = () => {
    if (isAuthorized && isIndianCitizen) {
      dispatch(saveUserDeclaration({
        politicallyExposed: !isPoliticallyNotExposed,
        indianCitizen: true,
        settlementTimeFrame: '90days',
      }));
    }
  };

  const onChange = (e: any): void => {
    if (!checkState) {
      if (e.target.id === 'isAuthorized') {
        setIsAuthorized((prev) => !prev);
      } else if (e.target.id === 'isPoliticallyNotExposed') {
        setIsPoliticallyNotExposed((prev) => !prev);
      } else if (e.target.id === 'isIndianCitizen') {
        setIsIndianCitizen((prev) => !prev);
      }
    }
  };

  useEffect(() => {
    if (digioState.status === 'success') {
      if (digioState.isUserSignSaved && digioState.isUserDeclarationSaved) {
        navigate('/add-nominee');
        // setIsNomineeModalOpen(true);
        notifications.show({
          color: 'green',
          message: 'User declaration saved successfully!',
          icon: <IconSuccess />,
        });
        dispatch(setDigioFormStatus('idle'));
      } else {
        // dispatch(saveUserSign({ file: signFile.current }));
        previewSignOpen()
        setOpen(false);
      }
    } else if (digioState.status === 'error') {
      dispatch(clearDigioFormError());
      notifications.show({
        color: 'red',
        message: digioState.error.message || 'Something went wrong, Try again!',
        icon: <IconError />,
      });
    }
  }, [digioState.status]);

  useEffect(() => {
    if (fnoActivationState.status === 'success') {
      dispatch(setFnoActivationFormStatus('idle'));
      notifications.show({
        color: 'orange',
        message: 'Fno Trade Activation Skipped!',
        icon: <IconInfoCircle />,
      });
      navigate('/aadhaar-esign');
    } else if (fnoActivationState.status === 'error') {
      dispatch(clearFnoActivationFormError());
      notifications.show({
        color: 'red',
        message: fnoActivationState.error.message || 'Could\'nt skip fno details, Try again!',
        icon: <IconError />,
      });
    }
  }, [fnoActivationState.status]);

  const uploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }
    const file: File = e.target.files[0];
    signFile.current = file;

    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();

      reader.onloadend = () => {
        setImagePreviewUrl(reader.result as string);
      };

      reader.readAsDataURL(file);
    }

    onNextClick();
  };

  const onSave = (): void => {
    // setImageURL(sigCanvas?.current?.getTrimmedCanvas()?.toDataURL('image/png'));
    signFile.current = new File(
      [sigCanvas?.current?.getTrimmedCanvas()?.toDataURL('image/png')],
      'fileName.png',
      { type: 'image/png' }
    );
    // dispatch(saveUserSign({ file: signFile.current }));
    onNextClick();
  };

  const onClear = (): void => {
    sigCanvas.current.clear();
    // setImageURL(null);
  };

  const handleDiscardSignatureImage = () => {
    setImagePreviewUrl(null);
    // previewSignClose();
  }

  const handleSaveSignatureImage = () => {
    dispatch(saveUserSign({ file: signFile.current }));
    TriggerEvent(SIGNATURE_EVENT.signatureUploadNext, {});
    previewSignClose();
  }

  useEffect(() => {
    if (!isAuthorized || !isIndianCitizen || !isPoliticallyNotExposed) {
      TriggerEvent(SIGNATURE_EVENT.signatureUpload, {
        signatureOptionsUnselected: true,
      });
    }
  }, [isAuthorized, isIndianCitizen, isPoliticallyNotExposed]);

  const renderFooter = (): JSX.Element => (
    <div className="csFooter">
      <Button radius={6} variant="outline" fullWidth onClick={previewSignOpen}>
        Upload Signature
      </Button>
    </div>
  );

  const fileUploadButton = (btnText:string, variant:string) => (
    <label
      htmlFor="signature-upload"
      className={variant === "primary"
        ? cx(classes.fileUploadButton, classes.fileUploadButtonPrimary) : classes.fileUploadButton}
    >
      <UploadIcon color={variant === "primary" ? "white" : "#963a72"} />

      {btnText}
      <input
        type="file"
        name="signature-upload"
        id="signature-upload"
        accept="image/*"
        className={classes.fileInput}
        onChange={uploadImage}
        ref={fileRef}
        disabled={false}
      />
    </label>
  )

  return (
    <ScreenWrapper>
      <div className="csMainContainer">
        <div className="csHeader">
          <div className="csProgressWrapper">
            <OnboardingProgress progress={getProgressStepper(currentStep)} />
          </div>
        </div>
        <div className={classes.contentContainer}>
          <div>
            <h2 className="csHeading">Signature Verification</h2>
            <p className="csDescription">
              Provide your valid signature to verify below declarations
            </p>
            <div>
              <label htmlFor="scales" className={classes.checkLabel}>
                <input
                  type="checkbox"
                  id="isAuthorized"
                  name="isAuthorized"
                  checked={isAuthorized || checkState}
                  onChange={onChange}
                  className={classes.checkBox}
                />
                <p className={cx(classes.inputDescription, classes.terms)}>
                  Running Account Authorization for 90 days.
                </p>
              </label>
              <label htmlFor="scales" className={classes.checkLabel}>
                <input
                  type="checkbox"
                  id="isPoliticallyNotExposed"
                  name="isPoliticallyNotExposed"
                  checked={isPoliticallyNotExposed || checkState}
                  onChange={onChange}
                  className={classes.checkBox}
                />
                <p className={cx(classes.inputDescription, classes.terms)}>
                  I&apos;m not a PEP (politically exposed person) or related to
                  a PEP.
                </p>
              </label>
              <label htmlFor="scales" className={classes.checkLabel}>
                <input
                  type="checkbox"
                  id="isIndianCitizen"
                  name="isIndianCitizen"
                  checked={isIndianCitizen || checkState}
                  onChange={onChange}
                  className={classes.checkBox}
                />
                <p className={cx(classes.inputDescription, classes.terms)}>
                  I&apos;m an Indian citizen and residing in India.
                </p>
              </label>
            </div>
          </div>
          <div className={classes.btnWrapper}>
            {/* temporary disabled this */}
            {/* <button
              type="button"
              disabled={false}
              className={cx(classes.fileUploadButton)}
              onClick={onModalOpen}
            >
              Draw
            </button> */}
          </div>
        </div>
        {renderFooter()}
      </div>
      <DrawSignModal
        opened={open}
        onClose={() => {
          setOpen(false);
        }}
        canvasRef={sigCanvas}
        onSave={onSave}
        onClear={onClear}
      />
      <PreviewSignatureModal
        opened={isPreviewSignOpened}
        onClose={previewSignClose}
        imagePreview={imagePreviewUrl}
        handleDiscardSignatureImage={handleDiscardSignatureImage}
        handleSaveSignatureImage={handleSaveSignatureImage}
        uploadButton={fileUploadButton}
      />
      {digioState.status === "loading" ? <ScreenLoader /> : null}
    </ScreenWrapper>
  );
};

export default SignOnboardingForm;
